/**
 * Central League Registry
 *
 * THE single source of truth for all supported leagues/sports.
 * Add a new sport here and every app picks it up automatically.
 *
 * When adding a new sport:
 *   1. Add the code to SportCode union
 *   2. Add the entry to SUPPORTED_SPORTS
 */

/** Union of all supported sport codes */
export type SportCode =
  | 'NFL'
  | 'NBA'
  | 'WNBA'
  | 'NCAAMB'
  | 'NCAAFB'
  | 'MLB'
  | 'PGA'
  | 'SOCCER'
  | 'MMA'
  | 'NASCAR'
  | 'TENNIS';

export interface LeagueConfig {
  /** Lowercase league identifier, e.g. 'nfl' */
  id: string;
  /** Full league name, e.g. 'National Football League' */
  name: string;
  /** Uppercase sport code, e.g. 'NFL' */
  code: SportCode;
  /** Path to league icon (for admin list pages) */
  iconSrc?: string;
  /** Label shown in filter dropdowns */
  filterLabel?: string;
  /**
   * Name of the icon component to render in the admin Sports sidebar.
   * Matches a key in `apps/admin/src/assets/Icon.tsx` so consumers can
   * iterate SUPPORTED_SPORTS instead of hardcoding the list of links.
   */
  sidebarIconName?: string;
  /** Label shown in the admin sidebar (defaults to filterLabel/name). */
  sidebarLabel?: string;
}

export const SUPPORTED_SPORTS: readonly LeagueConfig[] = [
  {
    id: 'nba',
    name: 'National Basketball Association',
    code: 'NBA',
    iconSrc: '/nba-logo.svg',
    filterLabel: 'NBA',
    sidebarIconName: 'Basketball',
    sidebarLabel: 'NBA',
  },
  {
    id: 'nfl',
    name: 'National Football League',
    code: 'NFL',
    iconSrc: '/league-icon.svg',
    filterLabel: 'NFL',
    sidebarIconName: 'Football',
    sidebarLabel: 'NFL',
  },
  {
    id: 'wnba',
    name: "Women's National Basketball Association",
    code: 'WNBA',
    iconSrc: '/basketball-logo.svg',
    filterLabel: 'WNBA',
    sidebarIconName: 'Basketball',
    sidebarLabel: 'WNBA',
  },
  {
    id: 'ncaamb',
    name: "NCAA Men's Basketball",
    code: 'NCAAMB',
    iconSrc: '/basketball-logo.svg',
    filterLabel: 'NCAAB',
    sidebarIconName: 'Basketball',
    sidebarLabel: 'NCAA Basketball',
  },
  {
    id: 'ncaafb',
    name: 'NCAA Football',
    code: 'NCAAFB',
    iconSrc: '/american-football-logo.svg',
    filterLabel: 'NCAAF',
    sidebarIconName: 'Football',
    sidebarLabel: 'NCAA Football',
  },
  {
    id: 'mlb',
    name: 'Major League Baseball',
    code: 'MLB',
    iconSrc: '/mlb-logo.svg',
    filterLabel: 'MLB',
    sidebarIconName: 'Baseball',
    sidebarLabel: 'MLB',
  },
  {
    id: 'pga',
    name: 'PGA Tour',
    code: 'PGA',
    iconSrc: '/pga-logo.svg',
    filterLabel: 'PGA',
    sidebarIconName: 'Golf',
    sidebarLabel: 'PGA',
  },
  /*
   * MMA is UFC. Deliberately NOT in INDIVIDUAL_SPORTS below — see the note there.
   *
   * `sidebarIconName` is still a stand-in: there is no boxing/MMA glyph in the admin icon set,
   * so it borrows Trophy until one exists.
   */
  {
    id: 'mma',
    name: 'MMA (UFC)',
    code: 'MMA',
    iconSrc: '/mma-logo.svg',
    filterLabel: 'MMA',
    sidebarIconName: 'Trophy',
    sidebarLabel: 'MMA',
  },
  /*
   * NASCAR (DKPLAT-843): the Cup, O'Reilly Auto Parts and Craftsman Truck series, merged into
   * one sport as soccer merges its competitions. A RACE: a field of drivers with no opponent, so a race is a
   * fixture that plays itself (home = away), as a PGA round does. Deliberately NOT in
   * INDIVIDUAL_SPORTS — that flag renders the golf tournament UI, and a race is a single dated
   * event the ordinary date-driven screens already handle. See docs/plans/nascar-integration.md.
   */
  {
    id: 'nascar',
    name: 'NASCAR',
    code: 'NASCAR',
    iconSrc: '/nascar-logo.svg',
    filterLabel: 'NASCAR',
    sidebarIconName: 'FlagCheckered',
    sidebarLabel: 'NASCAR',
  },
  /*
   * Tennis (DKPLAT-844): ATP and WTA singles, one sport — a pickem can mix both tours, as a Grand
   * Slam day does. A MATCH has two real players with home/away qualifiers and a real score (sets
   * won), so it is an ordinary two-sided fixture, modelled as MMA is: a player is their own
   * "team" and also the player props bind to. Not in INDIVIDUAL_SPORTS (the golf UI).
   * See docs/plans/tennis-integration.md.
   */
  {
    id: 'tennis',
    name: 'Tennis',
    code: 'TENNIS',
    iconSrc: '/tennis-logo.svg',
    filterLabel: 'Tennis',
    sidebarIconName: 'Tennis',
    sidebarLabel: 'Tennis',
  },
  // Soccer is a single sport that internally covers multiple competitions
  // (Premier League, FIFA World Cup, etc.). Feed-service holds the list of
  // active Sportradar competition IDs and merges fixtures across them.
  {
    id: 'soccer',
    name: 'Soccer',
    code: 'SOCCER',
    iconSrc: '/soccer-logo.svg',
    filterLabel: 'Soccer',
    sidebarIconName: 'Soccer',
    sidebarLabel: 'Soccer',
  },
];

/** e.g. ['nfl', 'nba', 'ncaamb', 'mlb'] */
export const SUPPORTED_LEAGUE_IDS: readonly string[] = SUPPORTED_SPORTS.map(l => l.id);

/** e.g. ['NFL', 'NBA', 'NCAAMB', 'MLB'] */
export const SUPPORTED_SPORT_CODES: readonly string[] = SUPPORTED_SPORTS.map(l => l.code);

/** Map from uppercase sport code to lowercase league ID, e.g. { NFL: 'nfl', NBA: 'nba', ... } */
export const SPORT_TO_LEAGUE_ID: Record<string, string> = Object.fromEntries(
  SUPPORTED_SPORTS.map(l => [l.code, l.id])
);

/** Formatted list for error messages, e.g. '"nfl", "nba", "ncaamb", or "mlb"' */
export const LEAGUE_ID_LIST: string = SUPPORTED_LEAGUE_IDS.map(id => `"${id}"`).join(', ');

/** Runtime validation helper */
export function isValidLeagueId(id: string): boolean {
  return SUPPORTED_LEAGUE_IDS.includes(id.toLowerCase());
}

/** Sports that are individual (not team-based) */
/*
 * Sports with no two sides to a fixture.
 *
 * MMA is deliberately ABSENT. A bout has two real competitors carrying home/away qualifiers,
 * so it is a normal two-sided fixture — verified against the live feed, every bout sampled.
 * On this platform the flag does not mean "no teams", it means "render the PGA tournament UI"
 * (it drives ~7 admin components and suppresses date-based fixture queries), and none of that
 * is right for a fight card.
 */
export const INDIVIDUAL_SPORTS: readonly SportCode[] = ['PGA'];

/**
 * Sports with no team as a distinct entity, so no team-level question can settle.
 *
 * PGA has no opponent at all. In MMA a fighter IS its own team, so the team stat rows are empty
 * and a team question reads that emptiness as a genuine 0 — settling every option wrongly, which
 * is worse than not offering it.
 *
 * Separate from `INDIVIDUAL_SPORTS` on purpose: that flag means "render the PGA tournament UI"
 * and drives ~7 admin components, which is not right for a fight card. This one answers only the
 * narrower question the authoring dropdowns need.
 */
export const SPORTS_WITHOUT_TEAM_QUESTIONS: readonly SportCode[] = [
  'PGA',
  'MMA',
  'NASCAR',
  'TENNIS',
];

/**
 * Sports where a competitor IS a single player, so a roster view of that competitor lists them
 * on themselves.
 *
 * Narrower than `SPORTS_WITHOUT_TEAM_QUESTIONS`, and deliberately so: PGA is in that list
 * because a tournament has no opponent, but a PGA "team" IS a tournament and its player list is
 * the field of golfers — the one screen where golfers appear at all. Gating the roster views on
 * the question-type predicate hid it, which is why this exists separately.
 */
export const SPORTS_WHERE_COMPETITOR_IS_PLAYER: readonly SportCode[] = ['MMA', 'TENNIS'];

/** Whether this sport's competitor is itself a single player (so it has no roster to show). */
export function competitorIsPlayer(sport: string | undefined): boolean {
  if (!sport) return false;
  return SPORTS_WHERE_COMPETITOR_IS_PLAYER.includes(sport.toUpperCase() as SportCode);
}

/** Whether team-level question types should be offered for this sport. */
export function hasTeamQuestions(sport: string | undefined): boolean {
  if (!sport) return true;
  return !SPORTS_WITHOUT_TEAM_QUESTIONS.includes(sport.toUpperCase() as SportCode);
}

/**
 * Which question types a sport can actually GRADE.
 *
 * Absent means the full team-sport set, which is the right default: most sports have two sides,
 * a real score, and both team and player statistics.
 *
 * This replaced a binary `isIndividualSport` check, which could only express "golf or everything
 * else" and so offered MMA four markets that cannot settle. The distinction that matters is not
 * individual-vs-team, it is which of three things a sport actually has:
 *
 *   - two sides to a fixture          (MMA yes, PGA no)
 *   - a real numeric score            (MMA NO — its score is synthesised from the winner)
 *   - team statistics distinct from player ones (MMA no — a fighter is its own team)
 *
 * MMA has the first and not the others, which is why it takes the moneyline but not spread,
 * total, or anything team-stat based: `team_selection` would read its empty team stats as a
 * real 0 and settle every option wrongly, which is worse than not offering it.
 */
export const SPORT_QUESTION_TYPES: Partial<Record<SportCode, readonly string[]>> = {
  // No opponent: the tournament plays itself, so nothing two-sided can settle.
  PGA: ['player_selection', 'player_over_under', 'outcome'],
  /*
   * Two real fighters, so the moneyline works off the synthetic 1/0 score. Total and spread never
   * read that score: total counts rounds fought and spread settles on the method (see
   * `MAIN_MARKET_MEASURES`). The team-stat types have no team stats to read.
   */
  MMA: ['moneyline', 'player_selection', 'player_over_under', 'total', 'spread', 'outcome'],
  // A field of drivers with no opponent and no score: nothing two-sided can settle, and the race
  // "team" is the race itself, so team stats do not exist. Graded per driver.
  NASCAR: ['player_selection', 'player_over_under', 'outcome'],
  /*
   * Two real players and a real score (sets won), so the 2-way moneyline settles as any other
   * sport's. Total and spread count what `MAIN_MARKET_MEASURES` says (sets or games), and a
   * retirement voids whatever it left undecided. No team stats — a player is their own team.
   */
  TENNIS: ['moneyline', 'player_selection', 'player_over_under', 'total', 'spread', 'outcome'],
};

/** The full set, for any sport that does not narrow it. */
export const ALL_QUESTION_TYPES: readonly string[] = [
  'player_selection',
  'team_selection',
  'player_over_under',
  'team_over_under',
  'moneyline',
  'spread',
  'total',
];

/**
 * The question types this sport can grade. Unknown or undeclared sports get the full set, so a
 * new league is never silently limited by forgetting to add it here.
 */
export function questionTypesForSport(sport: string | undefined): readonly string[] {
  if (!sport) return ALL_QUESTION_TYPES;
  return SPORT_QUESTION_TYPES[sport.toUpperCase() as SportCode] ?? ALL_QUESTION_TYPES;
}

/**
 * Nouns for PROSE — generated question text a player reads, not UI labels.
 *
 * Distinct from `COMPETITOR_LABEL`, which is a plural, capitalised heading ("Fighters"). These
 * go inside sentences, so they are singular and lowercase. Without them the generated text says
 * "Which player will have the most Total Strikes?" and "Which team will win the Vieira vs
 * Bryczek match?" on a fight card — wrong in the product, not merely in admin.
 */
export interface SportNouns {
  /** The individual competitor: "player", "fighter", "golfer". */
  competitor: string;
  /** Whoever WINS a fixture: "team" for team sports, the competitor itself otherwise. */
  side: string;
  /** The fixture: "match", "bout". */
  contest: string;
  /** French "which {side}", which agrees in gender: "Quelle équipe", "Quel combattant". */
  whichSideFr: string;
}

const DEFAULT_NOUNS: SportNouns = {
  competitor: 'player',
  side: 'team',
  contest: 'match',
  whichSideFr: 'Quelle équipe',
};

const SPORT_NOUNS: Partial<Record<SportCode, Partial<SportNouns>>> = {
  MMA: { competitor: 'fighter', side: 'fighter', contest: 'bout', whichSideFr: 'Quel combattant' },
  PGA: { competitor: 'golfer', contest: 'tournament' },
  NASCAR: { competitor: 'driver', side: 'driver', contest: 'race', whichSideFr: 'Quel pilote' },
  TENNIS: { competitor: 'player', side: 'player', contest: 'match', whichSideFr: 'Quel joueur' },
};

/** Prose nouns for this sport, falling back to the team-sport defaults. */
export function sportNouns(sport: string | undefined): SportNouns {
  if (!sport) return DEFAULT_NOUNS;
  return { ...DEFAULT_NOUNS, ...(SPORT_NOUNS[sport.toUpperCase() as SportCode] ?? {}) };
}

/**
 * The sports whose feed names competitors surname-first — "Rosas Jr, Raul", "Molcan, Alex" —
 * and writes them into question text that way.
 */
const SURNAME_FIRST_SPORTS: ReadonlySet<string> = new Set<SportCode>(['MMA', 'TENNIS']);

/**
 * A competitor's name as a sentence or button should read it: "Raul Rosas Jr", not the feed's
 * "Rosas Jr, Raul". Only for {@link SURNAME_FIRST_SPORTS}. Stored names stay as the feed gives
 * them, because the card's `formatPlayerNameAsInitialSurname` needs the comma to keep a two-word
 * surname whole.
 */
export function readableCompetitorName(name: string, sport: string | undefined): string {
  if (!sport || !SURNAME_FIRST_SPORTS.has(sport.toUpperCase())) return name;
  const comma = name.indexOf(',');
  if (comma <= 0) return name;
  const given = name.slice(comma + 1).trim();
  const surname = name.slice(0, comma).trim();
  return given ? `${given} ${surname}` : surname;
}

/**
 * What this sport's "teams" are called, for admin screens.
 *
 * The sports pages are generic — one table, one set of buttons, every sport. That is why MMA
 * appeared with no admin code at all, and also why it says "Teams" above a list of fighters and
 * "Tournaments" nowhere. Naming is the only sport-specific thing these screens need.
 */
export const COMPETITOR_LABEL: Partial<Record<SportCode, string>> = {
  PGA: 'Tournaments',
  MMA: 'Fighters',
  /*
   * A race is the "team" whose players are its entry list — that is how drivers become pickable —
   * so this list is each race's field. The races themselves are the fixtures in the sport page's
   * table; calling this list "Races" as well showed the same races twice under two names.
   */
  NASCAR: 'Entry lists',
  TENNIS: 'Players',
};

/**
 * How far either side of today a sport's fixture list looks when no dates are chosen.
 *
 * The sports page otherwise shows TODAY only, which suits a sport with games most days and leaves
 * a weekly one empty on six days out of seven — a NASCAR race runs on a weekend, so a Monday showed
 * "No matches found" and looked like a sport with no fixtures. Only sports whose feed can serve a
 * range cheaply belong here: NASCAR reads its whole season schedule in one cached request, whereas
 * MMA fetches one day at a time, so a three-week default would cost it three weeks of requests.
 */
export const DEFAULT_FIXTURE_WINDOW: Partial<
  Record<SportCode, { daysBack: number; daysForward: number }>
> = {
  NASCAR: { daysBack: 7, daysForward: 14 },
};

/** The default fixture window for this sport, or null when it defaults to today only. */
export function defaultFixtureWindow(
  sport: string | undefined
): { daysBack: number; daysForward: number } | null {
  if (!sport) return null;
  return DEFAULT_FIXTURE_WINDOW[sport.toUpperCase() as SportCode] ?? null;
}

/** "Teams" unless the sport calls them something else. */
export function competitorLabelForSport(sport: string | undefined): string {
  if (!sport) return 'Teams';
  return COMPETITOR_LABEL[sport.toUpperCase() as SportCode] ?? 'Teams';
}

/**
 * Sports whose score is SYNTHESISED rather than reported by the feed.
 *
 * MMA is the only one: a bout has no score at all, just a winner and a method, so the
 * transformer writes 1/0 to let the score-based graders settle it. That number is a grading
 * device, not a result — printing it as "0 : 1" invites an operator to read a fight as a
 * football scoreline, so the admin shows who beat whom instead.
 */
export const SPORTS_WITH_SYNTHETIC_SCORE: readonly SportCode[] = ['MMA'];

export function hasSyntheticScore(sport: string | undefined): boolean {
  if (!sport) return false;
  return SPORTS_WITH_SYNTHETIC_SCORE.includes(sport.toUpperCase() as SportCode);
}

/** Check if a sport is individual (not team-based) */
export function isIndividualSport(sport: string): boolean {
  return INDIVIDUAL_SPORTS.includes(sport.toUpperCase() as SportCode);
}

/**
 * What a Total or Spread counts, for a sport where the answer is not simply its score.
 *
 * `source` says where the figure for each side comes from:
 *  - `score`        — the fixture score, as every other sport (tennis: sets won).
 *  - `player_stat`  — the side's player stat (tennis: games won); a total adds both sides.
 *  - `shared`       — a fight-level stat both sides carry the same (MMA: rounds fought).
 *  - `mma_judges`   — MMA spread: a finish is covered by its winner; a decision needs the
 *                     judges' totals, entered by an admin as a score override.
 */
export interface MainMarketMeasure {
  statTypeCode: string;
  /** Coach label: "Games". */
  label: string;
  /** Lowercase noun for the question text: "games". */
  noun: string;
  source: 'score' | 'player_stat' | 'shared' | 'mma_judges';
}

export const MAIN_MARKET_MEASURES: Partial<
  Record<SportCode, Partial<Record<'total' | 'spread', readonly MainMarketMeasure[]>>>
> = {
  TENNIS: {
    total: [
      { statTypeCode: 'points', label: 'Sets', noun: 'sets', source: 'score' },
      { statTypeCode: 'tennis_games_won', label: 'Games', noun: 'games', source: 'player_stat' },
    ],
    spread: [{ statTypeCode: 'tennis_games_won', label: 'Games', noun: 'games', source: 'player_stat' }],
  },
  MMA: {
    total: [{ statTypeCode: 'mma_rounds_fought', label: 'Rounds', noun: 'rounds', source: 'shared' }],
    spread: [{ statTypeCode: 'points', label: "Judges' points", noun: 'points', source: 'mma_judges' }],
  },
};

/** The measures a sport's Total or Spread can count, or none for a sport that counts its score. */
export function mainMarketMeasures(
  sport: string | undefined,
  questionType: string | undefined
): readonly MainMarketMeasure[] {
  if (!sport || (questionType !== 'total' && questionType !== 'spread')) return [];
  return MAIN_MARKET_MEASURES[sport.toUpperCase() as SportCode]?.[questionType] ?? [];
}

/** The measure a stored Total or Spread counts, from its sport and stat code. */
export function mainMarketMeasureFor(
  sport: string | undefined,
  questionType: string | undefined,
  statTypeCode: string | null | undefined
): MainMarketMeasure | undefined {
  return mainMarketMeasures(sport, questionType).find(m => m.statTypeCode === statTypeCode);
}
